/* eslint-disable no-await-in-loop */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-restricted-syntax */
/* eslint-disable react/jsx-no-bind */
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Step,
	StepLabel,
	Stepper
} from "@mui/material";
import { useRef, useState } from "react";
import MDButton from "components/Basics/MDButton";
import MDBox from "components/Basics/MDBox";
import i18n from "i18n";
import StepImport from "./1. Import";
import { useDispatch } from "react-redux";
import { t } from "i18next";

import sourceActions from "redux-react/actions/sourceActions";
import { createAssets } from "redux-react/reducers/assetReducer";
import { v4 } from "uuid";
import { getSignedUrl, uploadToSignedUrl } from "helpers/s3";
import AddAssetStepSelectFiles from "../components/AddAssetStepSelectFiles";

/**
 * Add external video/audio dialog
 * User can upload files or insert a link
 * @param {object} props - Component props
 * @param {boolean} props.open - Dialog open state
 * @param {function} props.onClose - Close callback
 * @param {string} props.folderId - Target folder ID
 * @param {function} props.getFolders - Refresh folders callback
 * @returns {JSX.Element} AddExternalVideo
 */
export default function AddExternalVideo({
	open,
	onClose,
	format = "videos",
	folderId,
	getFolders = () => {}
}) {
	const dispatch = useDispatch();
	const topContainer = useRef(null);

	const [importing, setImporting] = useState(false);

	/**
	 * Active step
	 */
	const [activeStep, setActiveStep] = useState(0);

	/**
	 * Steps labels
	 */
	const steps = [t("SOURCES.STEPS.upload"), t("SOURCES.STEPS.filesChoice")];

	/**
	 * Can user go to next step
	 */
	const [stepValid, setStepValid] = useState(false);

	const [skeleton, setSkeleton] = useState({
		selectedRows: [], // Selected rows in table
		files: [], // Imported files
		filesToProcess: [], // {url, name, alreadyExists, image?} files selected by user to process,
		importMethod: "upload" // "upload" or "link"
	});

	/**
	 * Close dialog
	 */
	function close() {
		setImporting(false);
		setActiveStep(0);
		setStepValid(false);
		setSkeleton({
			selectedRows: [],
			files: [],
			filesToProcess: [],
			importMethod: "upload"
		});
		onClose();
	}

	/**
	 * User can go to next step
	 */
	const validStep = (val = true) => {
		setStepValid(val);
	};

	/**
	 * Go to next step
	 */
	const handleNext = () => {
		setActiveStep(activeStep + 1);
		setStepValid(false);
		topContainer?.current?.scrollIntoView();
	};

	/**
	 * Transform files informations to correct files format
	 * @param {Array<object>} update - New skeleton values
	 */
	const handleSaveFilesSelection = update => {
		setSkeleton(skeleton => {
			return {
				...skeleton,
				...update
			};
		});

		validStep();
		handleNext();
	};

	/**
	 * Upload file to S3
	 * @param {File} file - File to upload
	 * @returns {Promise} - Promise object represents the result of the upload
	 */
	async function uploadFileToS3(file) {
		const path = `sources/${v4()}`;
		const { success, error, result } = await getSignedUrl(file, {}, path);

		if (!success) {
			return {
				success: false,
				error
			};
		}

		const signedUrl = result.signedUrl;
		const fileObject = result.fileObject;

		await uploadToSignedUrl(signedUrl, file);

		return {
			success: true,
			result: {
				signedUrl,
				fileObject
			}
		};
	}

	/**
	 * Update files
	 * @param {Array<object>} files - Updated files
	 */
	const onChangeFiles = files => {
		setSkeleton(skeleton => {
			return {
				...skeleton,
				files
			};
		});
	};

	const uploadFileToS3Controller = async (localFiles, url) => {
		const localFile = localFiles.find(file => file.name === url);
		const { success, error, result } = await uploadFileToS3(localFile);
		if (success) {
			return {
				success: true,
				result: result.fileObject
			};
		} else {
			return {
				success: false
			};
		}
	};

	/**
	 * Import video/audio files
	 */
	const importMedia = async () => {
		setImporting(true);
		const localFiles = skeleton.localFiles ?? [];
		const files = skeleton.files.filter(file => skeleton.selectedRows.includes(file.url));

		const filesPayload = [];

		if (skeleton.importMethod === "upload" && files.length > 0) {
			// Upload local files to S3
			for (let file of files) {
				const { success, result } = await uploadFileToS3Controller(localFiles, file.url);
				if (success) {
					filesPayload.push({
						name: file.name,
						folderId,
						fileType: "videos",
						language: file.language,
						alreadyExists: file.alreadyExists,
						content: [result],
						metadatas: {}
					});
				}
			}
		} else if (skeleton.importMethod === "link") {
			// Add external link
			for (let file of files) {
				filesPayload.push({
					name: file.url,
					folderId,
					fileType: "videos",
					language: file.language,
					alreadyExists: file.alreadyExists,
					content: file.url,
					metadatas: {},
					extra: file.extra || {}
				});
			}
		}

		if (filesPayload.length === 0) {
			setImporting(false);
			return;
		}

		const onSuccessCallback = assets => {
			const formattedAssets = assets.map(asset => ({ ...asset, id: asset._id }));
			dispatch(createAssets(formattedAssets));
			getFolders();
			close();
		};

		dispatch(sourceActions.createManyAssets({ files: filesPayload }, onSuccessCallback));
	};

	/**
	 * Get actual step content
	 */
	function getStepContent(stepIndex) {
		switch (stepIndex) {
			case 0: {
				return (
					<StepImport
						skeleton={skeleton}
						handleSave={handleSaveFilesSelection}
						validStep={validStep}
					/>
				);
			}
			case 1: {
				return (
					<AddAssetStepSelectFiles
						format={format}
						skeleton={skeleton}
						setSkeleton={setSkeleton}
						onChange={onChangeFiles}
						validStep={validStep}
					/>
				);
			}
			default:
				return null;
		}
	}

	/**
	 * Get next button according to step
	 * @returns {JSX.Element|null} - Next button or null
	 */
	const getNextButton = () => {
		switch (activeStep) {
			case 0:
				return null;
			case 1: {
				return (
					<MDButton
						disabled={!stepValid || skeleton.selectedRows.length < 1}
						variant="contained"
						color="info"
						onClick={importMedia}
					>
						{`${t("SETTINGS.add")} (${skeleton.selectedRows.length}) ${t("SOURCES.nextFiles")}`}
					</MDButton>
				);
			}
			default:
				return null;
		}
	};

	return (
		<Dialog
			fullWidth
			maxWidth="xxl"
			PaperProps={{
				sx: {
					height: "90%"
				}
			}}
			open={open}
			onClose={close}
		>
			<DialogTitle>
				<MDBox>
					<Stepper activeStep={activeStep} alternativeLabel>
						{steps.map(label => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				</MDBox>
			</DialogTitle>
			<DialogContent>
				<div data-id="top-container" ref={topContainer}></div>
				<MDBox mt={2} style={{ height: "90%" }}>
					{getStepContent(activeStep)}
				</MDBox>
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={close}>
					{i18n.t("SETTINGS.cancel")}
				</MDButton>
				{getNextButton()}
			</DialogActions>
		</Dialog>
	);
}
